<!-- Loading State -->
<div *ngIf="isLoading()" class="d-flex justify-content-center align-items-center py-5">
  <div class="spinner-border text-primary" role="status">
    <span class="visually-hidden">Loading...</span>
  </div>
</div>

<!-- Error State -->
<div *ngIf="!isLoading() && errorMessage()" class="alert alert-danger">
  <i class="bi bi-exclamation-triangle me-2"></i>
  {{ errorMessage() }}
  <button class="btn btn-link p-0 ms-2" (click)="goBack()">Return to Field Tests</button>
</div>

<!-- Content -->
<ng-container *ngIf="!isLoading() && !errorMessage() && test()">
  <!-- Page Header -->
  <div class="d-flex justify-content-between align-items-start mb-4">
    <div>
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb mb-1">
          <li class="breadcrumb-item"><a routerLink="/logbook/testing/field">Field Tests</a></li>
          <li class="breadcrumb-item active">{{ test()!.title }}</li>
        </ol>
      </nav>
      <h1 class="h2 mb-0 fw-bold">{{ test()!.title }}</h1>
    </div>
    <div class="btn-group">
      <button class="btn btn-outline-secondary" (click)="goBack()">
        <i class="bi bi-arrow-left me-2"></i>Back
      </button>
      <button class="btn btn-outline-primary" (click)="editTest()">
        <i class="bi bi-pencil me-2"></i>Edit
      </button>
      <button class="btn btn-outline-danger" (click)="confirmDelete()">
        <i class="bi bi-trash me-2"></i>Delete
      </button>
    </div>
  </div>

  <div class="row g-4">
    <!-- Left Column - Main Content -->
    <div class="col-lg-8">
      <!-- Test Information -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-info-circle me-2"></i>Test Information</h5>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label text-muted small mb-1">Test Type</label>
              <p class="mb-0">
                <i class="bi me-2" [ngClass]="getTestTypeIcon(test()!.testType)"></i>
                {{ getTestTypeLabel(test()!.testType) }}
              </p>
            </div>
            <div class="col-md-6">
              <label class="form-label text-muted small mb-1">Category</label>
              <p class="mb-0">
                <i class="bi bi-tag me-2"></i>
                {{ getCategoryLabel(test()!.category) }}
              </p>
            </div>
            <div class="col-md-6">
              <label class="form-label text-muted small mb-1">Date Performed</label>
              <p class="mb-0">
                <i class="bi bi-calendar me-2"></i>
                {{ formatDate(test()!.datePerformed) }}
              </p>
            </div>
            <div class="col-md-6">
              <label class="form-label text-muted small mb-1">Applicable Aircraft</label>
              <p class="mb-0">
                <i class="icon-drone me-2"></i>
                {{ getAircraftDisplayName(test()!.applicableAircraft) }}
              </p>
            </div>
            <div class="col-md-6">
              <label class="form-label text-muted small mb-1">Frequency</label>
              <p class="mb-0">
                <i class="bi bi-arrow-repeat me-2"></i>
                {{ getFrequencyLabel(test()!.frequency) }}
              </p>
            </div>
            <div class="col-md-6">
              <label class="form-label text-muted small mb-1">Requires Signoff</label>
              <p class="mb-0">
                <i class="bi me-2" [ngClass]="test()!.requiresSignoff ? 'bi-check-circle text-success' : 'bi-x-circle text-muted'"></i>
                {{ test()!.requiresSignoff ? 'Yes' : 'No' }}
              </p>
            </div>
            <div class="col-12" *ngIf="test()!.description">
              <label class="form-label text-muted small mb-1">Description</label>
              <p class="mb-0">{{ test()!.description }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Attached Files -->
      <div class="card mb-4 mb-lg-0">
        <div class="card-header p-3">
          <div class="d-flex justify-content-between align-items-center">
            <h5 class="mb-0"><i class="bi bi-paperclip me-2"></i>Attached Files</h5>
            <span class="badge bg-secondary">{{ test()!.files.length || 0 }} file(s)</span>
          </div>
        </div>
        <div class="card-body p-0">
          <ng-container *ngIf="test()!.files && test()!.files.length > 0; else noFiles">
            <div class="list-group list-group-flush">
              <button *ngFor="let file of test()!.files"
                 (click)="openFile(file)"
                 class="list-group-item list-group-item-action d-flex align-items-center gap-3 py-3 text-start border-0">
                <div class="file-icon-lg">
                  <i class="bi" [ngClass]="getFileIcon(file.type)"></i>
                </div>
                <div class="flex-grow-1">
                  <div class="fw-medium">{{ file.name }}</div>
                  <div class="small text-muted" *ngIf="file.size">{{ formatFileSize(file.size) }}</div>
                </div>
                <div class="text-muted">
                  <i class="bi bi-box-arrow-up-right"></i>
                </div>
              </button>
            </div>
          </ng-container>
          <ng-template #noFiles>
            <div class="text-center py-4">
              <i class="bi bi-file-earmark text-muted fs-2-5rem"></i>
              <p class="text-muted mt-2 mb-0">No files attached</p>
            </div>
          </ng-template>
        </div>
      </div>
    </div>

    <!-- Right Column - Sidebar -->
    <div class="col-lg-4">
      <div class="sticky-top sticky-sidebar">
      <!-- Status Card -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0">Test Preview</h5>
        </div>
        <div class="card-body text-center">
          <div class="mb-3">
            <span class="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary size-80px">
              <i class="bi text-white fs-2-5rem" [ngClass]="getTestTypeIcon(test()!.testType)"></i>
            </span>
          </div>
          <h5 class="mb-1">{{ test()!.title }}</h5>
          <div class="d-flex justify-content-center gap-2 mb-3">
            <span class="badge bg-info">{{ getTestTypeLabel(test()!.testType) }}</span>
            <span class="badge bg-secondary" *ngIf="test()!.category">{{ getCategoryLabel(test()!.category) }}</span>
          </div>
          <p class="small text-muted mb-0" *ngIf="test()!.description; else noDesc">
            {{ test()!.description }}
          </p>
          <ng-template #noDesc>
            <p class="small text-muted fst-italic mb-0">No description</p>
          </ng-template>
        </div>
      </div>

      <!-- Quick Stats -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0">Quick Stats</h5>
        </div>
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <span class="text-muted">Files Attached</span>
            <span class="fw-medium">{{ test()!.files.length || 0 }}</span>
          </div>
          <div class="d-flex justify-content-between align-items-center mb-3">
            <span class="text-muted">Usage Count</span>
            <span class="fw-medium">{{ test()!.usageCount || 0 }}</span>
          </div>
          <div class="d-flex justify-content-between align-items-center">
            <span class="text-muted">Created</span>
            <span class="fw-medium">{{ formatDate(test()!.createdDate) }}</span>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="card">
        <div class="card-header p-3">
          <h5 class="mb-0">Actions</h5>
        </div>
        <div class="card-body">
          <div class="d-grid gap-2">
            <button class="btn btn-outline-primary" (click)="editTest()">
              <i class="bi bi-pencil me-2"></i>Edit Test
            </button>
            <button class="btn btn-outline-danger" (click)="confirmDelete()">
              <i class="bi bi-trash me-2"></i>Delete Test
            </button>
            <button class="btn btn-outline-secondary" (click)="goBack()">
              <i class="bi bi-arrow-left me-2"></i>Back to Field Tests
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  </div>
</ng-container>

<!-- Confirm Delete Modal -->
<app-confirm-modal
  #confirmModal
  (confirmed)="onDeleteConfirmed()"
  (cancelled)="onDeleteCancelled()"
></app-confirm-modal>
